import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

// No static data - all data comes from backend API

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Helper to get auth config
  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // Don't fetch data automatically - let pages fetch when needed

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await axios.get(`${API_BASE_URL}/users`, getAuthConfig());
      // Handle different response formats
      const usersData = Array.isArray(response.data) 
        ? response.data 
        : response.data.data || response.data.users || [];
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/products`, getAuthConfig());
      const productsData = Array.isArray(response.data) 
        ? response.data 
        : response.data.data || response.data.products || [];
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const response = await axios.get(`${API_BASE_URL}/orders`, getAuthConfig());
      const ordersData = Array.isArray(response.data) 
        ? response.data 
        : response.data.data || response.data.orders || [];
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Product CRUD operations
  const addProduct = async (productFormData) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };
      const response = await axios.post(`${API_BASE_URL}/products`, productFormData, config);
      const newProduct = response.data.data || response.data;
      setProducts([...products, newProduct]);
      return { success: true, product: newProduct };
    } catch (error) {
      console.error('Error adding product:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to add product' };
    }
  };

  const updateProduct = async (id, productFormData) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };
      const response = await axios.put(`${API_BASE_URL}/products/${id}`, productFormData, config);
      const updated = response.data.data || response.data;
      setProducts(products.map(p => (p._id || p.id) === id ? updated : p));
      return { success: true, product: updated };
    } catch (error) {
      console.error('Error updating product:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update product' };
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/products/${id}`, getAuthConfig());
      setProducts(products.filter(p => (p._id || p.id) !== id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting product:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to delete product' };
    }
  };

  // User operations
  const deleteUser = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/users/${id}`, getAuthConfig());
      setUsers(users.filter(u => u.id !== id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to delete user' };
    }
  };

  const addUser = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users`, userData, getAuthConfig());
      setUsers([...users, response.data]);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Error adding user:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to add user' };
    }
  };

  const updateUser = async (id, userData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/users/${id}`, userData, getAuthConfig());
      setUsers(users.map(u => u.id === id ? response.data : u));
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update user' };
    }
  };

  // Order operations
  const updateOrder = async (id, orderData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/orders/${id}`, orderData, getAuthConfig());
      const updated = response.data.data || response.data;
      // Update the orders list with the new data
      setOrders(orders.map(o => {
        if ((o._id || o.id) === id) {
          // Merge the updated data with existing order data
          return { ...o, ...updated, ...orderData };
        }
        return o;
      }));
      return { success: true, order: { ...updated, ...orderData } };
    } catch (error) {
      console.error('Error updating order:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update order' };
    }
  };

  const deleteOrder = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/orders/${id}`, getAuthConfig());
      setOrders(orders.filter(o => (o._id || o.id) !== id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting order:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to delete order' };
    }
  };

  const value = {
    products,
    orders,
    users,
    usersLoading,
    productsLoading,
    ordersLoading,
    addProduct,
    updateProduct,
    deleteProduct,
    deleteUser,
    addUser,
    updateUser,
    updateOrder,
    deleteOrder,
    fetchUsers,
    fetchProducts,
    fetchOrders
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
